import React, { useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import { Replay } from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import { ConfirmDialog } from '../ui';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { useOrganizations } from '../../contexts/OrganizationContext';
import { canManageIntegrationCredentials } from '../../utils/integrationCredentials';
import { shopifyShopLabel } from './ResetShopifyLinkButton';

/**
 * Resume Motzu pricing for one product on one Shopify integration (MOT-470, flag
 * `shopify_respect_store_prices`). The push leaves a price or compare-at the store changed in
 * Shopify alone; this lets the next sync write Motzu's again. Confirmed first, because that sync
 * replaces what the store set. Owner/admin only, as the API requires; everyone else sees nothing.
 */
export function ResumeMotzuPricingButton({
  integrationId,
  productId,
  shopDomain = null,
  showShopInLabel = false,
}: {
  integrationId: string;
  productId: string;
  shopDomain?: string | null;
  showShopInLabel?: boolean;
}) {
  const { t } = useAppTranslation();
  const { showToast } = useToast();
  const { userProfile } = useAuth();
  const { actingOrganization } = useOrganizations();
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const shop = shopifyShopLabel(shopDomain);

  const canResume = canManageIntegrationCredentials(
    actingOrganization?.role,
    (userProfile as { role?: string } | null)?.role,
  );

  const mutation = useMutation({
    mutationFn: () => apiService.resumeShopifyPricing(integrationId, productId),
    onSuccess: () => {
      setConfirmOpen(false);
      queryClient.invalidateQueries({ queryKey: ['erp-sync-audit', productId] });
      showToast(t('integrations.resumeShopifyPricing.done'), 'success');
    },
    onError: (err: Error) => {
      setConfirmOpen(false);
      showToast(err.message || t('integrations.resumeShopifyPricing.failed'), 'error');
    },
  });

  if (!canResume) return null;

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={mutation.isPending ? <CircularProgress size={14} /> : <Replay />}
        onClick={() => setConfirmOpen(true)}
        disabled={mutation.isPending}
      >
        {showShopInLabel && shop
          ? t('integrations.resumeShopifyPricing.actionForShop', { shop })
          : t('integrations.resumeShopifyPricing.action')}
      </Button>
      <ConfirmDialog
        open={confirmOpen}
        title={
          shop
            ? t('integrations.resumeShopifyPricing.confirmTitleForShop', { shop })
            : t('integrations.resumeShopifyPricing.confirmTitle')
        }
        message={t('integrations.resumeShopifyPricing.confirmBody')}
        confirmLabel={t('integrations.resumeShopifyPricing.confirmLabel')}
        cancelLabel={t('common.cancel')}
        confirmColor="warning"
        loading={mutation.isPending}
        onConfirm={() => mutation.mutate()}
        onClose={() => {
          if (!mutation.isPending) setConfirmOpen(false);
        }}
      />
    </>
  );
}
